import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Hash, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const NumberNinja = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const grade = user?.grade_level || 7;
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(1);
  const [problem, setProblem] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);
  const totalRounds = 10;

  const generateProblem = useCallback(() => {
    const grade6Types = [
      () => { const a = Math.floor(Math.random()*20)+1; const b = Math.floor(Math.random()*20)+1; return { display: `${a} + ___ = ${a+b}`, answer: b }; },
      () => { const a = Math.floor(Math.random()*30)+10; const b = Math.floor(Math.random()*a)+1; return { display: `${a} - ___ = ${a-b}`, answer: b }; },
      () => { const a = Math.floor(Math.random()*10)+2; const b = Math.floor(Math.random()*10)+2; return { display: `${a} x ___ = ${a*b}`, answer: b }; },
    ];
    const grade7Types = [
      ...grade6Types,
      () => { const b = Math.floor(Math.random()*10)+2; const c = Math.floor(Math.random()*10)+1; return { display: `___ ÷ ${b} = ${c}`, answer: b*c }; },
      () => { const a = Math.floor(Math.random()*15)+1; const b = Math.floor(Math.random()*15)+1; const c = Math.floor(Math.random()*15)+1; return { display: `${a} + ${b} + ___ = ${a+b+c}`, answer: c }; },
      () => { const a = Math.floor(Math.random()*5)+2; const b = Math.floor(Math.random()*10)+1; return { display: `${a}² + ___ = ${a*a + b}`, answer: b }; },
    ];
    const grade8Types = [
      ...grade7Types,
      () => { const a = Math.floor(Math.random()*10)+2; const b = Math.floor(Math.random()*5)+2; return { display: `${a}² - ${b}² = ___`, answer: a*a - b*b }; },
      () => { const a = Math.floor(Math.random()*5)+2; return { display: `√___ = ${a}`, answer: a*a }; },
      () => { const a = Math.floor(Math.random()*20)+5; const p = 10+Math.floor(Math.random()*20); const ans = Math.round(a*p/100); return { display: `${p}% of ${a} = ___`, answer: ans }; },
      () => { const a = Math.floor(Math.random()*10)+1; const b = Math.floor(Math.random()*10)+1; return { display: `(-${a}) × (-${b}) = ___`, answer: a*b }; },
    ];
    
    const types = grade >= 8 ? grade8Types : grade >= 7 ? grade7Types : grade6Types;
    const fn = types[Math.floor(Math.random() * types.length)];
    setProblem(fn());
    setUserInput('');
    setFeedback(null);
  }, []);

  useEffect(() => { generateProblem(); }, [generateProblem]);

  const checkAnswer = () => {
    const correct = parseInt(userInput) === problem.answer;
    if (correct) { setScore(s => s + 15); setFeedback({ correct: true, msg: 'Ninja strike! Perfect answer!' }); }
    else setFeedback({ correct: false, msg: `The missing number was ${problem.answer}` });
    setTimeout(() => { if (round < totalRounds) { setRound(r => r+1); generateProblem(); } else setCompleted(true); }, 1200);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-2xl max-w-md w-full text-center">
          <div className="text-7xl mb-4">🥷</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Number Ninja!</h2>
          <p className="text-6xl font-black text-emerald-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">points earned out of {totalRounds * 15}</p>
          <div className="space-y-3">
            <button onClick={() => { setScore(0); setRound(1); setCompleted(false); generateProblem(); }} className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-bold text-lg border-b-4 border-emerald-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold text-lg">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50" data-testid="number-ninja-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex items-center gap-3">
            <span className="bg-emerald-500 text-white px-4 py-2 rounded-2xl font-bold">{round}/{totalRounds}</span>
            <span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span>
          </div>
        </div>
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl text-center">
          <Hash className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-[#1E293B] mb-6">Find the Missing Number!</h2>
          <p className="text-5xl font-black text-[#1E293B] mb-8 tracking-wider">{problem?.display}</p>
          <input type="number" value={userInput} onChange={(e) => setUserInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && userInput && checkAnswer()}
            className="w-40 text-center text-4xl font-black p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none mx-auto block mb-6"
            placeholder="?" autoFocus data-testid="number-input" />
          {feedback && <div className={`p-4 rounded-2xl font-bold text-lg mb-4 ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{feedback.correct ? <CheckCircle className="w-5 h-5 inline mr-2" /> : <XCircle className="w-5 h-5 inline mr-2" />}{feedback.msg}</div>}
          <button onClick={checkAnswer} disabled={!userInput} className="px-10 py-4 bg-emerald-500 text-white rounded-2xl font-bold text-lg border-b-4 border-emerald-700 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50" data-testid="submit-ninja-btn">Submit</button>
        </div>
      </div>
    </div>
  );
};

export default NumberNinja;
